import { Client } from "@stomp/stompjs";
import { useCallback, useEffect, useRef, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
// Core layouts and components (not lazy loaded - needed immediately)
import PrivateOutlet from "./layout/PrivateOutlet";
import PublicOutlet from "./layout/PublicOutlet";
import Loading from "./components/Loading";
import ErrorBoundary from "./components/ErrorBoundary";
import store, { saveState } from "./redux/store";
import {
  CASINO_SERVICE_URL,
  createSocket,
  CRICKET_SERVICE_URL,
  IS_PROD,
  SOCCER_AND_TENNIS_SERVICE_URL,
} from "./utils/constants";
import { getAllCricketMatchListAPI, getAllCricketMatchesDashboardAPI } from "./service/cricket";
import { getAllSoccerMatchListAPI, getAllTennisMatchListAPI, getAllMatchesOddsSoccerAPI, getAllMatchesOddsTennisAPI } from "./service/soccer&tennis";

// PERFORMANCE FIX: Lazy load pages to reduce initial bundle size
// This reduces initial load from ~2MB to ~400KB
// Pages are loaded on-demand when user navigates to them

// Main pages - loaded quickly as they're common
const Dashboard = lazy(() => import("./pages/dashboard"));
const GamePage = lazy(() => import("./pages/game/GamePage"));
const Login = lazy(() => import("./pages/login"));
const LoginAuth = lazy(() => import("./pages/login/loginAuth"));

// Report pages - lazy loaded
const AccountStatement = lazy(() => import("./pages/accountStatement/AccountStatement"));
const ActivityLog = lazy(() => import("./pages/activityLog/ActivityLog"));
const BetHistory = lazy(() => import("./pages/betHistory/BetHistory"));
const ProfitLossReport = lazy(() => import("./pages/profitLossReport/ProfitLossReport"));
const UnSetteledBet = lazy(() => import("./pages/unSetteledBet/UnSetteledBet"));
const CasinoResult = lazy(() => import("./pages/casinoResults/CasinoResults"));

// Settings pages
const SecurityAuth = lazy(() => import("./pages/settings/SecurityAuth"));
const ChangeButtonValue = lazy(() => import("./pages/ChangeButtonValue"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));

// Casino pages - lazy loaded (44 games!)
const CasinoList = lazy(() => import("./pages/casino/CasinoList"));
const Cards32A = lazy(() => import("./pages/casino/games/32Cards-A"));
const Cards32B = lazy(() => import("./pages/casino/games/32Cards-B"));
const JudgementCards3 = lazy(() => import("./pages/casino/games/3CardsJudgement"));
const AmarAkbarAnthony = lazy(() => import("./pages/casino/games/AmabAkbarAnthony"));
const AndarBahar = lazy(() => import("./pages/casino/games/AndarBahar"));
const AndarBahar2 = lazy(() => import("./pages/casino/games/AndarBahar-2"));
const BollywoodCasino = lazy(() => import("./pages/casino/games/BollywoodCasino"));
const CasinoMeter = lazy(() => import("./pages/casino/games/CasinoMeter"));
const CasinoQueen = lazy(() => import("./pages/casino/games/CasinoQueen"));
const DragonTiger1Day = lazy(() => import("./pages/casino/games/DragonTiger1Day"));
const DragonTiger202 = lazy(() => import("./pages/casino/games/DragonTiger202"));
const DragonTiger201 = lazy(() => import("./pages/casino/games/DragonTiger201"));
const DragonTigerLion = lazy(() => import("./pages/casino/games/DragonTigerLion"));
const InstantWorli = lazy(() => import("./pages/casino/games/InstantWorli"));
const Lucky7A = lazy(() => import("./pages/casino/games/Lucky7A"));
const Lucky7B = lazy(() => import("./pages/casino/games/Lucky7B"));
const Poker20 = lazy(() => import("./pages/casino/games/Poker20-20"));
const Poker1Day = lazy(() => import("./pages/casino/games/Poker1Day"));
const Pokerr6Players = lazy(() => import("./pages/casino/games/Poker6Players"));
const Race2020 = lazy(() => import("./pages/casino/games/Race2020"));
const TeenPatti = lazy(() => import("./pages/casino/games/TeenPatti"));
const TeenPatti1Day = lazy(() => import("./pages/casino/games/Teenpatti1Day"));
const TeenPattiTest = lazy(() => import("./pages/casino/games/TeenPattiTest"));
const TeenPattiOpen = lazy(() => import("./pages/casino/games/TeenPattiOpen"));
const VVIPTeenPatti = lazy(() => import("./pages/casino/games/VVIPTeenPatti"));
const Dolidana = lazy(() => import("./pages/casino/games/Dolidana"));
const Mogambo = lazy(() => import("./pages/casino/games/Mogambo"));
const Lucky6 = lazy(() => import("./pages/casino/games/Lucky6"));
const BeachRoulette = lazy(() => import("./pages/casino/games/BeachRoulette"));
const Roulette13 = lazy(() => import("./pages/casino/games/Roulette13"));
const GoldenRoulette = lazy(() => import("./pages/casino/games/GoldenRoulette"));
const TeenPattiPoison = lazy(() => import("./pages/casino/games/TeenPattiPoison"));
const TeenPattiPoison20 = lazy(() => import("./pages/casino/games/TeenPattiPoison20"));
const UnlimitedJoker20 = lazy(() => import("./pages/casino/games/UnlimitedJoker20"));
const TeenPattiJoker20 = lazy(() => import("./pages/casino/games/TeenPattiJoker20"));
const UnlimitedJokerOneday = lazy(() => import("./pages/casino/games/UnlimitedJokerOneday"));
const TeenPatti20C = lazy(() => import("./pages/casino/games/TeenPatti20C"));
const BollywoodCasino2 = lazy(() => import("./pages/casino/games/BollywoodCasino2"));
const UniqueTeenPatti = lazy(() => import("./pages/casino/games/UniqueTeenPatti"));
const UniqueRoulette = lazy(() => import("./pages/casino/games/UniqueRoulette"));
const CasinoWar = lazy(() => import("./pages/casino/games/CasinoWar"));
const FiveCricket = lazy(() => import("./pages/casino/games/Five-5Cricket"));
const Five5Cricket = lazy(() => import("./pages/casino/games/Five-5Cricket")); // Alias for FiveCricket
const Cricket2020 = lazy(() => import("./pages/casino/games/Cricket20-20"));
const Baccart = lazy(() => import("./pages/casino/games/Baccart"));
const Baccart2 = lazy(() => import("./pages/casino/games/Baccarat2"));
const Worli = lazy(() => import("./pages/casino/games/WorliMatka"));
const SuperOver = lazy(() => import("./pages/casino/games/SuperOver"));
const MiniSuperOver = lazy(() => import("./pages/casino/games/MiniSuperOver"));
const Goal = lazy(() => import("./pages/casino/games/Goal"));
const AndarBahar150 = lazy(() => import("./pages/casino/games/AndarBahar150"));
const Lucky15 = lazy(() => import("./pages/casino/games/Lucky15"));

// Slot & Fantasy pages
const Slot = lazy(() => import("./pages/slot/slot"));
const Fantasy = lazy(() => import("./pages/fantasyGame/fantasyGame"));

// Other pages
const Video = lazy(() => import("./pages/Video"));
const Home = lazy(() => import("./pages/home"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const LastResults = lazy(() => import("./components/LastResults"));

export default function App() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.account.userData);
  const location = useLocation();

  // Track active STOMP clients so we can pause/resume on visibility change
  const stompClientsRef = useRef([]);

  const disconnectStomp = useCallback((client) => {
    if (!client) return;
    try {
      client.deactivate();
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (location.pathname.includes("live-casino")) {
      const gameId_ = location.pathname.split("/")[2];
      if (gameId_) {
        const socket = createSocket("casino", dispatch, gameId_);
        return () => disconnectStomp(socket);
      }
    }
  }, [location.pathname, dispatch, disconnectStomp]);

  // Fetch match data + connect WebSockets on home/dashboard pages
  const isHomePage = location.pathname === "/" || location.pathname.startsWith("/games");
  const isLoggedIn = !!userData?.token;
  useEffect(() => {
    // Skip data fetching when user must reset password — prevents 401 race
    // conditions and unnecessary load while the change-password modal is shown.
    if (!isLoggedIn || !isHomePage || userData?.resetRequired) return;

    const fetchAllMatches = async () => {
      const [cricketRes, soccerRes, tennisRes] = await Promise.all([
        getAllCricketMatchListAPI(),
        getAllSoccerMatchListAPI(),
        getAllTennisMatchListAPI(),
      ]);
      const cricketData = (cricketRes?.code === 200 && cricketRes.response)
        ? (Array.isArray(cricketRes.response) ? cricketRes.response : [])
        : [];
      dispatch({ type: "gameData/setCricketGames", payload: cricketData });

      const soccerData = (soccerRes?.code === 200 && soccerRes.response)
        ? (Array.isArray(soccerRes.response)
            ? soccerRes.response
            : soccerRes.response?.soccerAllMatchesResponseDTOS || [])
        : [];
      dispatch({ type: "gameData/setSoccerGames", payload: soccerData });

      const tennisData = (tennisRes?.code === 200 && tennisRes.response)
        ? (Array.isArray(tennisRes.response)
            ? tennisRes.response
            : tennisRes.response?.tennisAllMatchesResponseDTOS || [])
        : [];
      dispatch({ type: "gameData/setTennisGames", payload: tennisData });

      dispatch({ type: "gameData/setIsDataLoaded", payload: true });
    };
    fetchAllMatches();

    // Fetch odds via HTTP in parallel — gives instant odds instead of
    // waiting 2-3s for WebSocket handshake + first push.
    // WebSocket still takes over for real-time updates after connecting.
    const fetchInitialOdds = async () => {
      try {
        const [cricketOddsRes, soccerOddsRes, tennisOddsRes] = await Promise.all([
          getAllCricketMatchesDashboardAPI().catch(() => null),
          getAllMatchesOddsSoccerAPI().catch(() => null),
          getAllMatchesOddsTennisAPI().catch(() => null),
        ]);
        if (cricketOddsRes?.code === 200 && cricketOddsRes.response) {
          dispatch({ type: "gameData/setCricketOdds", payload: cricketOddsRes.response });
        }
        if (soccerOddsRes?.code === 200 && soccerOddsRes.response) {
          dispatch({ type: "gameData/setSoccerOdds", payload: soccerOddsRes.response });
        }
        if (tennisOddsRes?.code === 200 && tennisOddsRes.response) {
          dispatch({ type: "gameData/setTennisOdds", payload: tennisOddsRes.response });
        }
      } catch (e) {
        // Silently ignore — WebSocket will deliver odds anyway
      }
    };
    fetchInitialOdds();

    const cricketStomp = getMatches("cricket");
    const soccerTennisStomp = getMatches("soccerAndTennis");
    stompClientsRef.current = [cricketStomp, soccerTennisStomp].filter(Boolean);

    return () => {
      disconnectStomp(cricketStomp);
      disconnectStomp(soccerTennisStomp);
      stompClientsRef.current = [];
    };
  }, [isLoggedIn, userData?.resetRequired, isHomePage, disconnectStomp]);

  // Pause WebSockets when tab is hidden, resume when visible.
  // Prevents reconnection storms and burst Redux dispatches on tab return.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const clients = stompClientsRef.current;
      if (!clients.length) return;

      if (document.hidden) {
        clients.forEach((client) => {
          try { client.deactivate(); } catch (e) {}
        });
      } else {
        clients.forEach((client) => {
          try { client.activate(); } catch (e) {}
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (userData?.token && userData?.resetRequired) {
      dispatch({
        type: "accountData/setChangePopSate",
        payload: true,
      });
    }
  }, [userData?.token, userData?.resetRequired]);

  // Auth state is restored via preloadedState in store.js (from localStorage["state"]).
  // No need to read from localStorage["user"] — it can hold a stale token
  // and overwrite the fresh one, causing 401 logout on reload.

  const getMatches = useCallback(
    (id) => {
      const isSoccerTennis = id == "soccerAndTennis" || id == "soccer" || id == "tennis";
      const stompURL =
        id == "cricket"
          ? !IS_PROD
            ? CRICKET_SERVICE_URL.replace("http", "ws").replace("/api", "/websocket")
            : CRICKET_SERVICE_URL.replace("http", "ws").replace("/v1", "") + "/websocket"
          : id == "casino"
          ? !IS_PROD
            ? CASINO_SERVICE_URL.replace("http", "ws").replace("/api", "/websocket")
            : CASINO_SERVICE_URL.replace("http", "ws").replace("/v1", "") + "/websocket"
          : !IS_PROD
          ? SOCCER_AND_TENNIS_SERVICE_URL.replace("http", "ws").replace("/api", "/websocket")
          : SOCCER_AND_TENNIS_SERVICE_URL.replace("http", "ws").replace("/v1", "") + "/websocket";

      const client = new Client({
        brokerURL: stompURL,
        reconnectDelay: 3000,
        debug: () => null,
        onConnect: () => {
          client.subscribe("/live/liveMatches", function (message) {
            try {
              let data = JSON.parse(message.body);
              if (id == "cricket")
                dispatch({ type: "gameData/mergeCricketGames", payload: data });
              if (isSoccerTennis) {
                dispatch({
                  type: "gameData/mergeSoccerGames",
                  payload: data.soccerAllMatchesResponseDTOS,
                });
                dispatch({
                  type: "gameData/mergeTennisGames",
                  payload: data.tennisAllMatchesResponseDTOS,
                });
              }
            } catch (e) {}
          });

          if (id == "soccerAndTennis") {
            client.subscribe("/live/soccer/liveOdds", function (message) {
              try {
                let data = JSON.parse(message.body);
                dispatch({ type: "gameData/setSoccerOdds", payload: data });
              } catch (e) {}
            });
            client.subscribe("/live/tennis/liveOdds", function (message) {
              try {
                let data = JSON.parse(message.body);
                dispatch({ type: "gameData/setTennisOdds", payload: data });
              } catch (e) {}
            });
          } else {
            let oddsUrl = "";
            if (id == "cricket") oddsUrl = "/live/liveOdds";
            if (id == "soccer") oddsUrl = "/live/soccer/liveOdds";
            if (id == "tennis") oddsUrl = "/live/tennis/liveOdds";
            if (oddsUrl) {
              client.subscribe(oddsUrl, function (message) {
                try {
                  let data = JSON.parse(message.body);
                  if (id == "cricket")
                    dispatch({ type: "gameData/setCricketOdds", payload: data });
                  if (id == "soccer")
                    dispatch({ type: "gameData/setSoccerOdds", payload: data });
                  if (id == "tennis")
                    dispatch({ type: "gameData/setTennisOdds", payload: data });
                } catch (e) {}
              });
            }
          }
        },
        onWebSocketClose: () => {
          console.warn("[getMatches] WebSocket closed for", id, "— will auto-reconnect");
        },
      });

      client.activate();
      return client;
    },
    [dispatch]
  );
  // Save account state on tab close/refresh so user stays logged in
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveState(store.getState());
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return userData?.token ? (
    <ErrorBoundary>
    <PrivateOutlet>
      {/* PERFORMANCE FIX: Wrap Routes in Suspense for lazy-loaded components */}
      <Suspense fallback={<Loading />}>
        <Routes>
        <Route exact path="/" element={<Dashboard />} />
        <Route exact path="/games/:sportName" element={<Dashboard />} />
        <Route exact path="/profile" element={<Dashboard />} />
        <Route exact path="/game/:game/:id" element={<GamePage />} />
        <Route path="/live-casino" element={<CasinoList />} />
        <Route path="/slots" element={<Slot />} />
        <Route path="/fantasy" element={<Fantasy />} />
        <Route
          path="/live-casino/dragonTiger202"
          element={<DragonTiger202 />}
        />
        <Route path="/live-casino/teenPatti20" element={<TeenPatti />} />
        <Route path="/live-casino/card32" element={<Cards32A />} />
        <Route path="/live-casino/card32eu" element={<Cards32B />} />
        <Route path="/live-casino/race20" element={<Race2020 />} />
        <Route path="/live-casino/casinoQueen" element={<CasinoQueen />} />
        <Route path="/live-casino/dragonTiger20" element={<DragonTiger201 />} />
        <Route
          path="/live-casino/dragonTigerOneDay"
          element={<DragonTiger1Day />}
        />
        <Route path="/live-casino/lucky7" element={<Lucky7A />} />
        <Route path="/live-casino/lucky7eu" element={<Lucky7B />} />
        <Route path="/live-casino/andarBahar20" element={<AndarBahar />} />
        <Route path="/live-casino/abj" element={<AndarBahar2 />} />
        <Route
          path="/live-casino/3cardsjudgement"
          element={<JudgementCards3 />}
        />
        <Route
          path="/live-casino/bollywoodTable"
          element={<BollywoodCasino />}
        />
        <Route path="/live-casino/aaa" element={<AmarAkbarAnthony />} />
        <Route path="/live-casino/teen" element={<TeenPatti1Day />} />
        <Route path="/live-casino/poker20" element={<Poker20 />} />
        <Route path="/live-casino/worli2" element={<InstantWorli />} />
        <Route path="/live-casino/casinoMeter" element={<CasinoMeter />} />
        <Route
          path="/live-casino/dragonTigerLion"
          element={<DragonTigerLion />}
        />
        <Route path="/live-casino/teen9" element={<TeenPattiTest />} />
        <Route path="/live-casino/teen8" element={<TeenPattiOpen />} />
        <Route path="/live-casino/teen62" element={<VVIPTeenPatti />} />
        <Route path="/live-casino/dolidana" element={<Dolidana />} />
        <Route path="/live-casino/mogambo" element={<Mogambo />} />
        <Route path="/live-casino/lucky5" element={<Lucky6 />} />
        <Route path="/live-casino/roulette12" element={<BeachRoulette />} />
        <Route path="/live-casino/roulette13" element={<Roulette13 />} />
        <Route path="/live-casino/roulette11" element={<GoldenRoulette />} />
        <Route path="/live-casino/poison" element={<TeenPattiPoison />} />
        <Route path="/live-casino/poison20" element={<TeenPattiPoison20 />} />
        <Route path="/live-casino/joker120" element={<UnlimitedJoker20 />} />
        <Route path="/live-casino/joker20" element={<TeenPattiJoker20 />} />
        <Route path="/live-casino/joker1" element={<UnlimitedJokerOneday />} />
        <Route path="/live-casino/teen20c" element={<TeenPatti20C />} />
        <Route path="/live-casino/btable2" element={<BollywoodCasino2 />} />
        <Route path="/live-casino/teenunique" element={<UniqueTeenPatti />} />
        <Route path="/live-casino/ourroullete" element={<UniqueRoulette />} />
        <Route path="/live-casino/casinoWar" element={<CasinoWar />} />
        <Route path="/live-casino/cricketV3" element={<FiveCricket />} />
        <Route path="/live-casino/cmatch20" element={<Cricket2020 />} />
        <Route path="/live-casino/poker" element={<Poker1Day />} />
        <Route path="/live-casino/poker9" element={<Pokerr6Players />} />
        <Route path="/live-casino/baccarat" element={<Baccart />} />
        <Route path="/live-casino/baccarat2" element={<Baccart2 />} />
        <Route path="/live-casino/worli" element={<Worli />} />
        <Route path="/live-casino/superOver" element={<SuperOver />} />
        <Route path="/live-casino/superover3" element={<MiniSuperOver />} />
        <Route path="/live-casino/goal" element={<Goal />} />
        <Route path="/live-casino/ab4" element={<AndarBahar150 />} />
        <Route path="/live-casino/lucky15" element={<Lucky15 />} />
        <Route
          path="/report/account-statement"
          element={<AccountStatement />}
        />
        <Route path="/report/bet-history" element={<BetHistory />} />
        <Route
          path="/report/profit-loss-report"
          element={<ProfitLossReport />}
        />
        <Route path="/report/unsetteled-bet" element={<UnSetteledBet />} />
        <Route path="/report/activity-log" element={<ActivityLog />} />
        <Route path="/report/casino-result" element={<CasinoResult />} />
        <Route path="/report/changebtnvalue" element={<ChangeButtonValue />} />
        <Route path="/report/changepassword" element={<ChangePasswordPage />} />
        <Route path="/settings/secureauth" element={<SecurityAuth />} />
      </Routes>
      </Suspense>
    </PrivateOutlet>
    </ErrorBoundary>
  ) : (
    <ErrorBoundary>
    <PublicOutlet>
      {/* PERFORMANCE FIX: Wrap Routes in Suspense for lazy-loaded components */}
      <Suspense fallback={<Loading />}>
        <Routes>
        <Route exact path="/" element={<Login />} />
        <Route exact path="/loginauth/2" element={<LoginAuth />} />
        <Route exact path="video" element={<Video />} />
        <Route path="/game/:game/:id" element={<GamePage />} />
        <Route path="/live-casino" element={<CasinoList />} />
        <Route path="/slots" element={<Slot />} />
        <Route path="/fantasy" element={<Fantasy />} />
        <Route path="/live-casino/dt2" element={<DragonTiger202 />} />
        <Route path="/live-casino/teenPatti20" element={<TeenPatti />} />
        <Route path="/live-casino/32cards-A" element={<Cards32A />} />
        <Route path="/live-casino/32cards-B" element={<Cards32B />} />
        <Route path="/live-casino/race20" element={<Race2020 />} />
        <Route path="/live-casino/casinoqueen" element={<CasinoQueen />} />
        <Route path="/live-casino/dt1" element={<DragonTiger201 />} />
        <Route
          path="/live-casino/dragonTigerOneDay"
          element={<DragonTiger1Day />}
        />
        <Route path="/live-casino/lucky7a" element={<Lucky7A />} />
        <Route path="/live-casino/lucky7b" element={<Lucky7B />} />
        <Route path="/live-casino/andarbahar" element={<AndarBahar />} />
        <Route path="/live-casino/abj" element={<AndarBahar2 />} />
        <Route
          path="/live-casino/3cardsjudgement"
          element={<JudgementCards3 />}
        />
        <Route path="/live-casino/btable" element={<BollywoodCasino />} />
        <Route path="/live-casino/aaa" element={<AmarAkbarAnthony />} />
        <Route path="/live-casino/teen" element={<TeenPatti1Day />} />
        <Route path="/live-casino/poker20" element={<Poker20 />} />
        <Route path="/live-casino/worli2" element={<InstantWorli />} />
        <Route path="/live-casino/cmeter" element={<CasinoMeter />} />
        <Route path="/live-casino/dtl" element={<DragonTigerLion />} />
        <Route path="/live-casino/teentest" element={<TeenPattiTest />} />
        <Route path="/live-casino/teenopen" element={<TeenPattiOpen />} />
        <Route path="/live-casino/teen62" element={<VVIPTeenPatti />} />
        <Route path="/live-casino/dolidana" element={<Dolidana />} />
        <Route path="/live-casino/mogambo" element={<Mogambo />} />
        <Route path="/live-casino/lucky5" element={<Lucky6 />} />
        <Route path="/live-casino/roulette12" element={<BeachRoulette />} />
        <Route path="/live-casino/roulette13" element={<Roulette13 />} />
        <Route path="/live-casino/roulette11" element={<GoldenRoulette />} />
        <Route path="/live-casino/poison" element={<TeenPattiPoison />} />
        <Route path="/live-casino/poison20" element={<TeenPattiPoison20 />} />
        <Route path="/live-casino/joker120" element={<UnlimitedJoker20 />} />
        <Route path="/live-casino/joker20" element={<TeenPattiJoker20 />} />
        <Route path="/live-casino/joker1" element={<UnlimitedJokerOneday />} />
        <Route path="/live-casino/teen20c" element={<TeenPatti20C />} />
        <Route path="/live-casino/btable2" element={<BollywoodCasino2 />} />
        <Route path="/live-casino/teenunique" element={<UniqueTeenPatti />} />
        <Route path="/live-casino/ourroullete" element={<UniqueRoulette />} />
        <Route path="/live-casino/war" element={<CasinoWar />} />
        <Route path="/live-casino/cricketv3" element={<Five5Cricket />} />
        <Route path="/live-casino/cmatch20" element={<Cricket2020 />} />
        <Route path="/live-casino/poker" element={<Poker1Day />} />
        <Route path="/live-casion/poker6" element={<Pokerr6Players />} />
        <Route path="/live-casion/baccarat" element={<Baccart />} />
        <Route path="/live-casion/baccarat2" element={<Baccart2 />} />
        <Route path="/live-casino/superover3" element={<MiniSuperOver />} />
        <Route path="/live-casino/goal" element={<Goal />} />
        <Route path="/live-casino/ab4" element={<AndarBahar150 />} />
        <Route path="/live-casino/lucky15" element={<Lucky15 />} />
      </Routes>
      </Suspense>
    </PublicOutlet>
    </ErrorBoundary>
  );
}
